<template>
  <div class="com-frame" ref="comFrameRef" tabindex="-1" outline="none">
    <div class="inner-wrap">
      <!-- 左侧：分录列表，表头固定 -->
      <div class="left-frame">
        <div class="table-caption">
          分录列表
          <span v-if="focusMode === 'voucher'" class="tag-active">【←→切换面板，按 ↑ ↓ 切换分录组】</span>
        </div>
        <div class="table-wrap" ref="tableWrapRef">
          <table class="voucher-table">
            <thead>
            <tr>
              <th class="col-icon"></th>
              <th width="90" class="text-center">校验结果</th>
              <th width="80">摘要</th>
              <th>科目</th>
              <th width="110">借方金额</th>
              <th width="110">贷方金额</th>
            </tr>
            </thead>
            <tbody>
            <tr
                v-for="(row, idx) in voucherTableData"
                :key="idx"
                :class="{ 'active-voucher-group': row.groupIndex === activeGroupIndex }"
                @click="onVoucherRowClick(row)"
            >
              <td class="icon-cell col-icon">
                <i v-if="focusMode === 'voucher' && isGroupFirstRow(row)" class="fa fa-hand-o-right"></i>
              </td>
              <td class="text-center">
                <span v-if="validationMap.has(row.djId)">
                  <i v-if="!validationMap.get(row.djId)" class="h-icon-error red-color"></i>
                  <i v-if="rowIsOtherMap.get(row.djId)" class="h-icon-warn warn-color"></i>
                  <i v-if="validationMap.get(row.djId) && !rowIsOtherMap.get(row.djId)" class="h-icon-success green-color"></i>
                </span>
                <span v-else class="validate-empty">-</span>
              </td>
              <td>{{ row.summary }}</td>
              <td>{{ row.subjectName }}</td>
              <td class="text-right">{{ numFormat(row.debitAmount) }}</td>
              <td class="text-right">{{ numFormat(row.creditAmount) }}</td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>
      <!-- 中间栏：附件列表 + 底部校验按钮区 -->
      <div class="mid-frame">
        <div class="panel-title">
          附件列表
          <span v-if="focusMode === 'attach'" class="tag-active">【←→切换面板，按 ↑ ↓ 切换附件】</span>
        </div>
        <ul class="h-menu menu-custom h-menu-mode-vertical" style="height:calc(100% - 135px);overflow:auto;">
          <li
              v-for="(menuItem, index) in dataList"
              class="h-menu-li h-menu-first-level"
              @click="menuClick(menuItem, index)"
              :ref="el => { if (index === selectedIndex) selectedEl = el }"
          >
            <div :class="'h-menu-show' + ((index === selectedIndex) ? ' h-menu-li-selected' : '')">
              <i
                  v-if="focusMode === 'attach' && index === selectedIndex"
                  class="fa fa-hand-o-right"
                  style="position:absolute;left:8px;"
              ></i>
              <span class="h-menu-show-icon">
                {{ index + 1 }} 、<i :class="menuItem.icon"></i>
              </span>
              <h-tooltip :content="menuItem.title" placement="top-start">
                <span class="h-menu-show-desc">{{ menuItem.title }}</span>
              </h-tooltip>
            </div>
          </li>
          <li v-if="dataList.length === 0" style="color:#999;text-align:center;padding:20px;">
            暂无可用附件
          </li>
        </ul>
        <!-- 底部：每个h‑button外层套div.btn-item，无size属性 -->
        <div class="attach-bottom-btn-wrap">
          <!-- 新增图标说明提示 -->
          <div class="tip-desc">
            <div class="tip-title">校验结果说明</div>
            <div><i class="h-icon-success green-color"></i>：校验通过</div>
            <div><i class="h-icon-error red-color"></i>：校验未通过</div>
            <div><i class="h-icon-warn warn-color"></i>：注意第三方材料</div>
          </div>
          <div class="btn-item">
            <h-button color="primary" @click="doValidateAll">校验</h-button>
          </div>
          <div class="btn-item">
            <h-button
                :color="hasPrevInvalid ? 'primary' : null"
                :disabled="!hasPrevInvalid"
                @click="goPrevInvalid"
            >上一处风险</h-button>
          </div>
          <div class="btn-item">
            <h-button
                :color="hasNextInvalid ? 'primary' : null"
                :disabled="!hasNextInvalid"
                @click="goNextInvalid"
            >下一处风险</h-button>
          </div>
        </div>
      </div>
      <!-- 右侧：预览区域 -->
      <div class="right-frame">
        <!-- 只有存在可用附件才渲染预览，无附件则空白，不显示旧iframe -->
        <template v-if="dataList.length > 0">
          <img
              v-if="selectedMenu.type === 'img'"
              :src="selectedMenu.url + '#navpanes=0'"
              title=""
              style="width:100%;height:100%;"
          />
          <iframe
              v-else
              :src="selectedMenu.url + '#navpanes=0'"
              title=""
              style="width:100%;height:100%;border:none;"
          ></iframe>
        </template>
        <div v-else class="preview-empty-holder">
          <i class="fa fa-file-o preview-empty-icon"></i>
          <div class="preview-empty-text">暂无附件预览</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { proxyPost } from '@/js/common/akstf';
import { mapState } from "vuex";
export default {
  name: "VoucherCheck",
  props: {
    companyCode: String,
    voucherData: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      focusMode: "voucher",
      activeGroupIndex: 0,
      voucherTableData: [],
      dataList: [],
      selectedIndex: 0,
      selectedEl: null,
      selectedMenu: {
        title: "",
        key: "",
        type: "pdf",
        icon: "fa fa-file-pdf-o",
        url: ""
      },
      validationMap: new Map(),
      rowIsOtherMap: new Map()
    };
  },
  computed: {
    ...mapState(['User', 'currentAccountSets']),
    groupIdList() {
      const set = new Set(this.voucherTableData.map(i => i.groupIndex));
      return Array.from(set).sort((a, b) => a - b);
    },
    currentVoucherGroup() {
      return this.voucherTableData.filter(i => i.groupIndex === this.activeGroupIndex);
    },
    currentRawAttach() {
      const groupRows = this.currentVoucherGroup;
      if (!groupRows || groupRows.length === 0) return [];
      return groupRows[0].billList || [];
    },
    invalidGroupList() {
      return this.groupIdList.filter(g => {
        const rows = this.voucherTableData.filter(r => r.groupIndex === g);
        return rows.some(row => this.validationMap.get(row.djId) === false);
      });
    },
    warnOrInvalidGroupList() {
      return this.groupIdList.filter(groupIdx => {
        const groupRows = this.voucherTableData.filter(r => r.groupIndex === groupIdx);
        const hasInvalidRow = groupRows.some(row => this.validationMap.get(row.djId) === false);
        const hasWarnRow = groupRows.some(row => this.rowIsOtherMap.get(row.djId) === true);
        return hasInvalidRow || hasWarnRow;
      });
    },
    // 将风险分组按照全部分录的原始顺序排序
    sortedRiskGroups() {
      return this.warnOrInvalidGroupList
          .slice()
          .sort((a, b) => this.groupIdList.indexOf(a) - this.groupIdList.indexOf(b));
    },
    currentGroupPos() {
      return this.groupIdList.indexOf(this.activeGroupIndex);
    },
    // 上一处风险：当前位置往前最近的风险分组
    prevRiskGroup() {
      const pos = this.currentGroupPos;
      if (pos === -1 || this.sortedRiskGroups.length === 0) return null;
      // 筛选风险分组下标 < 当前pos
      const arr = this.sortedRiskGroups.filter(g => this.groupIdList.indexOf(g) < pos);
      if (arr.length === 0) return null;
      // 取数组最后一个（离当前最近）
      return arr[arr.length - 1];
    },
    // 下一处风险：当前位置往后最近的风险分组
    nextRiskGroup() {
      const pos = this.currentGroupPos;
      if (pos === -1 || this.sortedRiskGroups.length === 0) return null;
      const arr = this.sortedRiskGroups.filter(g => this.groupIdList.indexOf(g) > pos);
      if (arr.length === 0) return null;
      return arr[0];
    },
    hasPrevInvalid() {
      return this.prevRiskGroup !== null;
    },
    hasNextInvalid() {
      return this.nextRiskGroup !== null;
    }
  },
  created() {
    this.buildGroupIndex();
    this.loadList();
  },
  mounted() {
    window.addEventListener("keydown", this.handleGlobalKeyDown);
  },
  beforeDestroy() {
    window.removeEventListener("keydown", this.handleGlobalKeyDown);
  },
  watch: {
    'voucherData.details': {
      handler() {
        this.buildGroupIndex();
        this.loadList();
        this.validationMap.clear();
        this.rowIsOtherMap.clear();
      },
      deep: true
    },
    activeGroupIndex(newVal) {
      // 防御：非法值直接return，不做赋值，避免watch循环触发
      if (newVal === undefined || newVal === null || this.groupIdList.indexOf(newVal) === -1) {
        return;
      }
      this.loadList();
      this.$nextTick(() => {
        this.scrollVoucherToView();
      });
    }
  },
  methods: {
    async doValidateAll() {
      this.validationMap.clear();
      this.rowIsOtherMap.clear();
      this.$Loading("请稍候...");
      const batchSize = 500;
      const allRows = [...this.voucherTableData];
      const batchList = [];
      for (let i = 0; i < allRows.length; i += batchSize) {
        const sliceRows = allRows.slice(i, i + batchSize);
        batchList.push({
          startIndex: i,
          rows: sliceRows
        });
      }
      const action = "/paperless/checkVoucher";
      try {
        for (const batchItem of batchList) {
          const { startIndex, rows: batchRows } = batchItem;
          const paramData = {
            "fydm": this.currentAccountSets.companyCode,
            "accountsetsid": this.currentAccountSets.id,
            "accountsetsname": this.currentAccountSets.companyName,
            "strdate": this.voucherData.voucherDate,
            "vouchercode": (this.voucherData.word + "-" + this.voucherData.code),
            "tablename": this.voucherData.details[0].dataType,
            "typename": this.voucherData.details[0].djType,
            "username": this.voucherData.createMemberName,
            "details": batchRows.map((row, localIdx) => ({
              id: row.djId,
              remark: row.summary,
              kmcode: row.subjectName,
              jfje: row.debitAmount,
              dfje: row.creditAmount,
              rowindex: startIndex + localIdx + 1
            }))
          };
          // --------模拟调试--------
          // const resultList = batchRows.map((row, localIdx) => {
          //   const globalRowIndex = startIndex - 1 + localIdx;
          //   if (globalRowIndex === 0) {
          //     return { id: row.djId, checknum: 1, checkList: ["模拟校验错误"], isOther: true };
          //   }
          //   if (globalRowIndex === 6) {
          //     return { id: row.djId, checknum: 1, checkList: ["模拟校验错误"], isOther: false };
          //   }
          //   if (globalRowIndex === 8) {
          //     return { id: row.djId, checknum: 0, checkList: [], isOther: true };
          //   }
          //   return { id: row.djId, checknum: 0, checkList: [], isOther: false };
          // });
          // ----真实接口打开下面两行----
          const respData = await proxyPost(action, paramData);
          const resultList = respData.data || [];
          this.$Loading.close();
          console.log("校验返回结果：")
          console.log(resultList)
          resultList.forEach(item => {
            const rowPass = item.checknum === 0 && (!item.checkList || item.checkList.length === 0);
            this.validationMap.set(String(item.id), rowPass);
            this.rowIsOtherMap.set(String(item.id), !!item.isOther);
          });

          console.log("校验异常的：")
          console.log(this.validationMap)

          console.log("有第三方材料的：")
          console.log(this.rowIsOtherMap)

          console.log("列表数据：")
          console.log(this.voucherTableData)
        }
        const allPass = [...this.validationMap.values()].every(flag => flag === true);
        return allPass;
      } catch (err) {
        this.$Message.error(`校验异常!`);
        throw err;
      }
    },
    goPrevInvalid() {
      if (!this.hasPrevInvalid) return;
      this.activeGroupIndex = this.prevRiskGroup;
      this.focusMode = "voucher";
    },
    goNextInvalid() {
      if (!this.hasNextInvalid) return;
      this.activeGroupIndex = this.nextRiskGroup;
      this.focusMode = "voucher";
    },
    handleGlobalKeyDown(e) {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        this.focusMode = "voucher";
        return;
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        this.focusMode = "attach";
        return;
      }
      if (this.focusMode === "voucher") {
        const currPos = this.groupIdList.indexOf(this.activeGroupIndex);
        if (e.key === "ArrowDown") {
          e.preventDefault();
          if (currPos < this.groupIdList.length - 1) {
            this.activeGroupIndex = this.groupIdList[currPos + 1];
          }
        } else if (e.key === "ArrowUp") {
          e.preventDefault();
          if (currPos > 0) {
            this.activeGroupIndex = this.groupIdList[currPos - 1];
          }
        }
      } else if (this.focusMode === "attach") {
        this.handleKeyDown(e);
      }
    },
    handleKeyDown(e) {
      if (e.key === "ArrowUp") {
        e.preventDefault();
        this.prevMenu();
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        this.nextMenu();
      }
    },
    isGroupFirstRow(row) {
      const group = this.currentVoucherGroup;
      if (!group || group.length === 0) return false;
      return row === group[0];
    },
    numFormat(val) {
      if (!val) return "";
      return Number(val).toLocaleString("zh-CN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    buildGroupIndex() {
      const list = Array.isArray(this.voucherData.details) ? [...this.voucherData.details] : [];
      if (!list.length) {
        this.voucherTableData = [];
        this.activeGroupIndex = 0;
        return;
      }
      let groupIndex = 0;
      const noticeMap = new Map();
      const djIdMap = new Map();
      list.forEach(item => {
        const noticeNo = item.noticeNo;
        const djId = item.djId;
        if (noticeNo !== null && noticeNo !== undefined && noticeNo !== "") {
          if (noticeMap.has(noticeNo)) {
            item.groupIndex = noticeMap.get(noticeNo);
          } else {
            noticeMap.set(noticeNo, groupIndex);
            item.groupIndex = groupIndex;
            groupIndex++;
          }
        } else {
          if (djIdMap.has(djId)) {
            item.groupIndex = djIdMap.get(djId);
          } else {
            djIdMap.set(djId, groupIndex);
            item.groupIndex = groupIndex;
            groupIndex++;
          }
        }
      });
      this.voucherTableData = list;
      this.activeGroupIndex = this.voucherTableData[0]?.groupIndex ?? 0;
    },
    loadList() {
      const list = this.currentRawAttach;
      this.dataList = [];
      this.selectedIndex = 0;
      // 原始没有附件
      if (!list || list.length === 0) {
        // ❗不修改 selectedMenu，保留右侧原有url
        return;
      }
      const tempList = [];
      for (let i = 0; i < list.length; i++) {
        const item = list[i];
        if (item.name.indexOf("回单") !== -1) continue;
        const menu = {
          title: item.name,
          key: item.uid,
          type: "pdf",
          icon: "fa fa-file-pdf-o",
          url: ""
        };
        switch (item.type) {
          case "application/pdf":
            menu.icon = "fa fa-file-pdf-o";
            menu.type = "pdf";
            break;
          case "image/jpeg":
          case "image/png":
          case "image/bmp":
          case "image/gif":
            menu.icon = "fa fa-file-image-o";
            menu.type = "img";
            break;
          case "text/plain":
            menu.icon = "fa fa-file-text-o";
            menu.type = "txt";
            break;
          case "application/msword":
          case "application/vnd.ms-excel":
            menu.icon = "fa fa-file-excel-o";
            menu.type = "excel";
            break;
          case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
          case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
            menu.icon = "fa fa-file-word-o";
            menu.type = "word";
            break;
          case "application/vnd.ms-powerpoint":
          case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
            menu.icon = "fa fa-file-powerpoint-o";
            menu.type = "ppt";
            break;
          case "application/x-zip-compressed":
            menu.icon = "fa fa-file-zip-o";
            menu.type = "zip";
            break;
          default:
            menu.icon = "fa fa-file-pdf-o";
            menu.type = "pdf";
        }
        if (item.isExternalLink) {
          menu.url = item.name.indexOf("审批") !== -1 ? item.withBankUrl : item.url;
        } else {
          menu.url = "/api" + item.url + "?name=" + item.name;
        }
        tempList.push(menu);
      }
      this.dataList = tempList;
      if (this.dataList.length > 0) {
        this.selectedMenu = this.dataList[0];
        this.selectedIndex = 0;
      } else {
        this.selectedMenu = { title: "", key: "", type: "pdf", icon: "fa fa-file-pdf-o", url: "" };
      }
    },
    menuClick(data, index) {
      this.focusMode = "attach";
      this.selectedMenu = data;
      this.selectedIndex = index;
    },
    prevMenu() {
      if (this.selectedIndex > 0) {
        this.selectedIndex--;
        this.selectedMenu = this.dataList[this.selectedIndex];
        this.$nextTick(() => this.scrollToSelected());
      }
    },
    nextMenu() {
      if (this.selectedIndex < this.dataList.length - 1) {
        this.selectedIndex++;
        this.selectedMenu = this.dataList[this.selectedIndex];
        this.$nextTick(() => this.scrollToSelected());
      }
    },
    scrollToSelected() {
      if (this.selectedEl) {
        this.selectedEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    },
    onVoucherRowClick(row) {
      this.focusMode = "voucher";
      this.activeGroupIndex = row.groupIndex;
    },
    scrollVoucherToView() {
      const wrap = this.$refs.tableWrapRef;
      if (!wrap) return;
      const rows = Array.from(wrap.querySelectorAll(".active-voucher-group"));
      if (!rows || rows.length === 0) return;
      const firstRow = rows[0];
      const lastRow = rows[rows.length - 1];
      const wrapRect = wrap.getBoundingClientRect();
      const firstRect = firstRow.getBoundingClientRect();
      const lastRect = lastRow.getBoundingClientRect();
      const headerHeight = 34;
      const firstTop = firstRect.top - wrapRect.top + wrap.scrollTop;
      const lastBottom = lastRect.bottom - wrapRect.top + wrap.scrollTop;
      const viewTop = wrap.scrollTop + headerHeight;
      const viewBottom = wrap.scrollTop + wrap.clientHeight;
      if (firstTop < viewTop) {
        wrap.scrollTop = firstTop - headerHeight;
      } else if (lastBottom > viewBottom) {
        wrap.scrollTop = lastBottom - wrap.clientHeight;
      }
    }
  }
};
</script>


<style lang="less" scoped>
.menu-custom {
  /deep/ .h-menu {
    &-show {
      color: #48576a;
      position: relative;
      &:hover {
        color: #20a0ff;
      }
    }
    &-li {
      &-selected {
        font-weight: bold;
        color: #20a0ff;
      }
    }
  }
}
.com-frame {
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  padding-left: 20px;
  padding-right: 20px;
  box-sizing: border-box;
  outline: none;
}
.inner-wrap {
  position: relative;
  width: 100%;
  height: 100%;
}
.left-frame {
  position: absolute;
  z-index: 2;
  top: 0;
  bottom: 0;
  width: 640px;
  border-right:1px solid #f0f0f0;
  display:flex;
  flex-direction:column;
}
.table-caption{
  height:40px;
  line-height:40px;
  padding:0 10px;
  font-weight:bold;
  border-bottom:1px solid #eee;
  flex-shrink: 0;
}
.table-wrap{
  flex:1;
  overflow:auto;
}
.voucher-table{
  width:100%;
  border-collapse: collapse;
  font-size:13px;
}
.voucher-table .col-icon{
  width:32px;
}
.icon-cell{
  padding-left:6px;
}
.voucher-table thead th {
  position: sticky;
  top:0;
  background:#fafafa;
  z-index: 10;
  height:34px;
}
.voucher-table th,
.voucher-table td{
  border:1px solid #ebeef5;
  padding:8px 6px;
}
.text-right{
  text-align:right;
}
.text-center{
  text-align:center;
}
.voucher-table tbody tr.active-voucher-group td {
  background-color: #fff9cc;
  font-weight: bold;
}
.mid-frame {
  position: absolute;
  left: 640px;
  top: 0;
  bottom: 0;
  width: 320px;
  overflow: hidden;
  z-index: 2;
  border-right:1px solid #f0f0f0;
  display: flex;
  flex-direction: column;
}
.panel-title {
  height:40px;
  line-height:40px;
  padding:0 10px;
  font-weight:bold;
  border-bottom:1px solid #eee;
  flex-shrink: 0;
}
.attach-bottom-btn-wrap{
  flex-shrink:0;
  padding:10px;
  border-top:1px solid #eee;
  display:flex;
  flex-direction: column;
  gap:8px;
  box-sizing: border-box;
  .btn-item{
    width:100%;
  }
  .btn-item button{
    width:100%;
  }
}
.right-frame {
  position: absolute;
  left: 960px;
  right: 0;
  top: 0;
  bottom: 0;
  padding: 0 0 5px 0;
  overflow: auto;
  z-index: 1;
}
.tag-active {
  margin-left:10px;
  font-size:12px;
  color:#409eff;
}
.validate-tag{
  display:inline-block;
  padding:2px 6px;
  border-radius:3px;
  font-size:12px;
  color:#fff;
}
.tag-pass{
  background:#67c23a;
}
.tag-fail{
  background:#f56c6c;
}
.validate-empty{
  color:#999;
}
.green-color {
  color:#67C23A;
}
.red-color {
  color:#F56C6C;
}
.warn-color {
  color:#E6A23C;
}
.h-icon-error, .h-icon-warn, .h-icon-success{
  margin:0 2px;
}
.tip-desc {
  font-size: 12px;
  color: #666;
  margin-bottom: 10px;
  line-height: 1.6;
  background-color: #f8f8f8;
  padding: 6px 8px;
  border-radius: 4px;
  > div {
    display: flex;
    align-items: center;
    gap: 4px;
  }
  .tip-title {
    font-weight: bold;
    font-size:13px;
    margin-bottom:4px;
    color:#333;
  }
}

.preview-empty-holder {
  width: 100%;
  height: 100%;
  background-color: #f8f8f8;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  .preview-empty-icon {
    font-size: 48px;
    color: #c0c4cc;
    margin-bottom: 12px;
  }
  .preview-empty-text {
    font-size: 14px;
    color: #909399;
  }
}

</style>
